import React from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import heroGroceries from "../assets/images/Home_page_image/bannerimage_1.png";
import Header from "../components/Header/userHeader";
import { UserFooter } from "../components";

const reasons = [
  {
    icon: <FavoriteBorderOutlinedIcon />,
    title: "Support the Local Economy",
    text: "Every single purchase you make on Sthanick directly supports a small business owner in your vicinity. You aren't just buying a product; you are investing in your community.",
  },
  {
    icon: <BoltOutlinedIcon />,
    title: "Unmatched Speed & Freshness",
    text: "Because your orders are fulfilled by stores just a few blocks away, you are guaranteed faster delivery times and the freshest products available.",
  },
  {
    icon: <SecurityOutlinedIcon />,
    title: "Familiar Trust, Digital Convenience",
    text: "Shop from the neighborhood vendors you have relied on for years, now enhanced with the modern convenience of digital payments, easy browsing, and doorstep delivery.",
  },
];

const AboutUs = () => {
  return (
    <Box sx={{ bgcolor: "#faf4ea", minHeight: "100vh", overflowX: "hidden" }}>
      <Header />

      <Box
        component="section"
        sx={{
          position: "relative",
          minHeight: { xs: 320, md: 350 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          textAlign: "center",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 8, md: 12.8 },
          px: 2,
          background:
            "linear-gradient(180deg, rgba(98, 38, 14, 0.9) 0%, rgba(126, 45, 16, 0.96) 46%, rgba(134, 48, 19, 1) 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${heroGroceries})`,
            backgroundRepeat: "repeat-x",
            backgroundPosition: "top center",
            backgroundSize: { xs: "330px auto", md: "460px auto" },
            opacity: 0.36,
            filter: "saturate(1.1) contrast(1.05)",
            transform: "scale(1.14)",
            transformOrigin: "top center",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(55, 23, 9, 0.18) 0%, rgba(126, 45, 16, 0.42) 24%, rgba(134, 48, 19, 0.86) 76%, rgba(134, 48, 19, 1) 100%)",
          },
        }}
      >
        <Box
          sx={{ position: "relative", zIndex: 1, maxWidth: 920, mx: "auto" }}
        >
          <Typography
            sx={{
              color: "#ff9a3c",
              fontSize: 16,
              fontWeight: 800,
              letterSpacing: 2.4,
              textTransform: "uppercase",
              mb: 2.3,
            }}
          >
            About Us
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 42, sm: 54, md: 64 },
              lineHeight: 1.05,
              fontWeight: 900,
              letterSpacing: 0,
              mb: 2.5,
            }}
          >
            Sthanick:{" "}
            <Box component="span" sx={{ color: "#ff8c2f" }}>
              Simply Local.
            </Box>
          </Typography>

          <Typography
            sx={{
              maxWidth: 820,
              mx: "auto",
              fontSize: { xs: 20, md: 25 },
              lineHeight: 1.32,
              fontWeight: 400,
              letterSpacing: 0.2,
            }}
          >
            Bringing your favourite local shops and service providers directly
            to your doorstep. Fast, reliable, and deeply connected to your
            community.
          </Typography>
        </Box>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 9.4 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 14 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: "#fff0dc",
                  color: "#ff6b1a",
                  display: "grid",
                  placeItems: "center",
                  mb: 3.2,
                }}
              >
                <StorefrontOutlinedIcon sx={{ fontSize: 34 }} />
              </Box>

              <Typography
                component="h2"
                sx={{
                  color: "#061226",
                  fontSize: { xs: 30, md: 34 },
                  fontWeight: 900,
                  letterSpacing: 0,
                  mb: 2.2,
                }}
              >
                Who We Are
              </Typography>

              <Typography
                sx={{
                  color: "#2e3b4d",
                  fontSize: 19,
                  lineHeight: 1.58,
                  maxWidth: 620,
                  mb: 3.2,
                  letterSpacing: 0.1,
                }}
              >
                <Box component="strong" sx={{ color: "#061226" }}>
                  Sthanick
                </Box>
                , an initiative by{" "}
                <Box component="strong" sx={{ color: "#061226" }}>
                  Arthavyavas Private Limited
                </Box>
                , was built on a simple yet powerful belief: the heart of any
                neighborhood lies in its local businesses.
              </Typography>

              <Typography
                sx={{
                  color: "#2e3b4d",
                  fontSize: 19,
                  lineHeight: 1.58,
                  maxWidth: 650,
                  letterSpacing: 0.1,
                }}
              >
                We are a hyper-local e-commerce platform dedicated to
                modernizing the way communities interact, shop, and grow
                together. Whether you need daily essentials or local services,
                we make sure they are just a tap away.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 4, md: 5 },
                  borderRadius: 4,
                  bgcolor: "#fff",
                  boxShadow: "0 14px 34px rgba(54, 41, 30, 0.08)",
                  maxWidth: 560,
                  ml: { md: "auto" },
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    color: "#061226",
                    fontSize: { xs: 25, md: 28 },
                    fontWeight: 900,
                    mb: 2.2,
                    letterSpacing: 0,
                  }}
                >
                  The Hyper-Local Model
                </Typography>

                <Typography
                  sx={{
                    color: "#314054",
                    fontSize: 17,
                    lineHeight: 1.6,
                    mb: 3.1,
                  }}
                >
                  We designed Sthanick to bridge the gap between neighborhood
                  stores, local service providers, and everyday customers. We
                  empower local businesses by digitizing their inventory and
                  operations, giving them the tools they need to thrive in a
                  digital-first world.
                </Typography>

                <Typography
                  sx={{ color: "#314054", fontSize: 17, lineHeight: 1.6 }}
                >
                  For customers, we provide unmatched convenience. You can now
                  shop from the stores you already know and trust, right from
                  your phone, without compromising on speed or quality.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          borderTop: "1px solid #efe4d4",
          borderBottom: "1px solid #efe4d4",
          py: { xs: 6.5, md: 8.8 },
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h2"
            sx={{
              color: "#7b2f10",
              fontSize: { xs: 24, md: 32 },
              fontWeight: 900,
              mb: 2.2,
            }}
          >
            Our Vision
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "#a4502c",
              fontSize: { xs: 17, md: 20 },
              lineHeight: 1.75,
              fontStyle: "italic",
              maxWidth: 800,
              mx: "auto",
            }}
          >
            "We aim to champion community commerce and completely modernize the
            neighborhood shopping experience. By keeping commerce local, we
            ensure faster deliveries, fresher products, and a thriving
            neighborhood economy where local entrepreneurs can seamlessly scale
            their businesses."
          </Typography>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 8, md: 9.5 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h2"
            align="center"
            sx={{
              color: "#071326",
              fontSize: { xs: 25, md: 32 },
              fontWeight: 900,
              mb: 1,
            }}
          >
            Why Choose Sthanick?
          </Typography>
          <Box
            sx={{
              width: 60,
              height: 3,
              bgcolor: "#ff6b1a",
              mx: "auto",
              mb: 5,
            }}
          />
          <Grid
            container
            spacing={3}
            alignItems="stretch"
            justifyContent="center"
          >
            {reasons.map((reason) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={reason.title}
                display="flex"
              >
                {/* ✅ FIXED CARD */}
                <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    minHeight: { xs: 230, md: 230 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: { xs: 3, md: 3.5 },
                    borderRadius: 1,
                    bgcolor: "#fff",
                    border: "1px solid #edf0f3",
                    boxShadow: "0 6px 16px rgba(9, 19, 33, 0.06)",
                    textAlign: "center",
                    transition: "all 0.3s ease",

                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 12px 24px rgba(9, 19, 33, 0.12)",
                    },
                  }}
                >
                  {/* ICON */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      mx: "auto",
                      mb: 2.2,
                      borderRadius: "50%",
                      bgcolor: "#ffe6cc",
                      color: "#ff6b1a",
                      display: "grid",
                      placeItems: "center",
                      "& svg": { fontSize: 24 },
                    }}
                  >
                    {reason.icon}
                  </Box>

                  <Typography
                    component="h3"
                    sx={{
                      color: "#020915",
                      fontSize: { xs: 17, md: 18 },
                      fontWeight: 900,
                      mb: 1.5,
                      maxWidth: 260,
                    }}
                  >
                    {reason.title}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#273447",
                      fontSize: { xs: 14, md: 15 },
                      lineHeight: 1.65,
                      maxWidth: 300,
                    }}
                  >
                    {reason.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          position: "relative",
          bgcolor: "#101827",
          color: "#fff",
          textAlign: "center",
          pt: { xs: 6, md: 6 },
          pb: { xs: 5.2, md: 5.8 },
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 0% 65%, rgba(255, 107, 26, 0.22), transparent 28%), radial-gradient(circle at 100% 15%, rgba(255, 107, 26, 0.16), transparent 22%)",
            opacity: 0.82,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: 32, md: 36 },
              fontWeight: 900,
              mb: 1.8,
              letterSpacing: 0,
            }}
          >
            Join the Movement
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 15 },
              lineHeight: 1.55,
              color: "#f2f5fb",
              mb: 2.8,
            }}
          >
            Whether you are a customer looking for quick daily essentials and
            services, or a local vendor ready to expand your digital footprint,{" "}
            <Box component="strong">Sthanick is your platform.</Box> As we roll
            out our services to neighborhoods across Hyderabad and beyond, we
            invite you to be a part of this journey.
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(255, 112, 41, 0.52)",
              bgcolor: "rgba(255, 107, 26, 0.12)",
              borderRadius: 1,
              color: "#ff9a3c",
              fontSize: { xs: 14, md: 16 },
              fontWeight: 900,
              py: 2,
              px: 2,
              mb: 2.8,
            }}
          >
            Together, let's build a stronger, more connected neighborhood.
          </Box>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="contained"
              startIcon={<ShoppingBagOutlinedIcon />}
              sx={{
                bgcolor: "#ff6b1a",
                color: "#fff",
                borderRadius: 6,
                px: 2.6,
                py: 0.8,
                minHeight: 34,
                fontSize: 12,
                fontWeight: 900,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#ef5e10", boxShadow: "none" },
                "& .MuiButton-startIcon": { mr: 0.5 },
                "& svg": { fontSize: "15px !important" },
              }}
            >
              Shop as Customer
            </Button>
            <Button
              variant="outlined"
              startIcon={<StorefrontOutlinedIcon />}
              sx={{
                borderColor: "#e3e8f2",
                color: "#fff",
                borderRadius: 6,
                px: 2.6,
                py: 0.8,
                minHeight: 34,
                fontSize: 12,
                fontWeight: 900,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
                "& .MuiButton-startIcon": { mr: 0.5 },
                "& svg": { fontSize: "15px !important" },
              }}
            >
              Partner as Vendor
            </Button>
          </Stack>
        </Container>
      </Box>

      <UserFooter />
    </Box>
  );
};

export default AboutUs;
